import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useOnboardingChecklist } from '@/hooks/useOnboardingChecklist';
import { Lightbulb, FolderPlus, Upload, MessageSquarePlus, Link as LinkIcon, Bot, ShieldCheck, MonitorCog } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';

type StepKey =
  | 'step_create_project'
  | 'step_upload_document'
  | 'step_start_chat'
  | 'step_assign_chat_to_project'
  | 'step_first_ai_message'
  | 'step_enable_trust'
  | 'step_enable_os_access';

type Item = {
  label: string;
  description: string;
  icon: React.ReactNode;
};

const items: Item[] = [
  { label: 'Create a Project', description: 'Set up your first project to keep work organized.', icon: <FolderPlus className="h-4 w-4 text-primary" /> },
  { label: 'Upload a Document', description: 'Add a document to your Knowledge Base.', icon: <Upload className="h-4 w-4 text-primary" /> },
  { label: 'Start a new Chat', description: 'Open a chat to begin collaborating with AI.', icon: <MessageSquarePlus className="h-4 w-4 text-primary" /> },
  { label: 'Assign Chat to a Project', description: 'Link a chat to your project for context.', icon: <LinkIcon className="h-4 w-4 text-primary" /> },
  { label: 'Send your first AI message', description: 'Talk to a model and get a response.', icon: <Bot className="h-4 w-4 text-primary" /> },
  { label: 'Enable Trust Mode', description: 'Keep your work safe and scoped.', icon: <ShieldCheck className="h-4 w-4 text-primary" /> },
  { label: 'Enable OS Access', description: 'Let your project appear inside MapleOS.', icon: <MonitorCog className="h-4 w-4 text-primary" /> },
];

const STEP_KEYS: StepKey[] = [
  'step_create_project',
  'step_upload_document',
  'step_start_chat',
  'step_assign_chat_to_project',
  'step_first_ai_message',
  'step_enable_trust',
  'step_enable_os_access',
];

export default function OnboardingChecklistModal() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { loading, state, progress, setStep, autoDetect } = useOnboardingChecklist();

  const shouldOpen = Boolean(user) && !loading && Boolean(state) && state!.completed === false;
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  useEffect(() => { setOpen(shouldOpen); }, [shouldOpen]);

  // Auto-detect progress each time the modal opens
  useEffect(() => {
    if (open) {
      autoDetect();
    }
  }, [open, autoDetect]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-w-[95vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary" />
            Get started: 7 quick steps
            <Badge variant="secondary" className="ml-2">
              {progress.count}/{progress.total}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Complete these steps to set up your workspace. This checklist will disappear when you're done.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          {state && items.map((item, idx) => {
            const key = STEP_KEYS[idx];
            const checked = (state as any)[key] as boolean;
            return (
              <div key={key} className="flex items-start gap-3 rounded-md border p-3">
                <div className="mt-1">{item.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{item.label}</div>
                    <Checkbox
                      checked={checked}
                      onCheckedChange={async (v) => {
                        const ok = await setStep(key, Boolean(v));
                        if (ok) {
                          toast({ title: checked ? 'Marked incomplete' : 'Marked complete' });
                        } else {
                          toast({ title: 'Could not update this step', description: 'Please try again or use Auto-check.', variant: 'destructive' });
                        }
                      }}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {item.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            Tip: Use the Auto-check button to detect recent activity (like new projects).
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={checking}
              onClick={async () => {
                setChecking(true);
                const res = await autoDetect();
                setChecking(false);
                if (res && typeof res === 'object' && 'ok' in res) {
                  const { ok, detected } = res as { ok: boolean; detected: Record<string, boolean> };
                  const updatedKeys = (res as any).updatedKeys as string[] | undefined;
                  const pretty = (keys: string[]) => keys.map(k => k.replace(/^step_/, '').replace(/_/g, ' '));
                  if (ok) {
                    if (updatedKeys && updatedKeys.length > 0) {
                      toast({
                        title: 'Progress detected',
                        description: `Updated: ${pretty(updatedKeys).join(', ')}`,
                      });
                    } else {
                      toast({
                        title: 'No new steps detected',
                        description: 'Try completing a step, then run Auto-check again.',
                      });
                    }
                  } else {
                    toast({ title: 'Auto-check failed', description: 'Please try again in a moment.', variant: 'destructive' });
                  }
                } else {
                  toast({ title: 'Auto-check unavailable', description: 'Please try again shortly.', variant: 'destructive' });
                }
              }}
            >
              {checking ? 'Checking…' : 'Auto-check'}
            </Button>
            <Button
              onClick={() => {
                autoDetect().then(() => {
                  // Modal will close automatically when all steps are completed
                });
              }}
            >
              I'm done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}